import { Box, Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@material-ui/core";
import { Link, LinkOff, MoreVert } from "@material-ui/icons";
import { Button, ConfirmDialog, DataGrid, GridColumnProps, SelectFilterComponent, SelectFloatingFilterComponent, StandardDialog, TextField } from "@roc/ui";
import { filterProps, noOpComparator } from "@roc/ui/utils";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useAppraisalStore } from "../hooks/useAppraisalStore";

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '65vh',
    overflow: 'hidden',
  },
}));

const APPRAISAL_DASHBOARD_COLUMNS: GridColumnProps[]=[
  {
    field:'loanId',
    headerName: 'Loan Id',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field:'amc',
    headerName: 'AMC',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field:'subtype',
    headerName: 'Subtype',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'orderId',
    headerName: 'Order Id',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'orderStatus',
    headerName: 'Order Status',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'propertyAddress',
    headerName: 'Property Address',
    minWidth: 200,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'action',
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
    width: 80,
    pinned: 'right',
    lockPinned: true,
  },
]

export const AppraisalTechDashboardGrid = observer(()=>{
  const classes = useStyles();
  const { appraisalStore } = useAppraisalStore();
  const { appraisalGridStore } = appraisalStore;
  const [orderIdToLink, setOrderIdToLink] = useState('');
  const [isLinkAppraisalOrderModalOpen, setLinkAppraisalOrderModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [record, setRecord] = useState<any>({});
  const [isConfirmLinkAppraisalOrderModalOpen, setConfirmLinkAppraisalOrderModalOpen] = useState(false);
  const [isConfirmUnLinkAppraisalOrderModalOpen, setConfirmUnLinkAppraisalOrderModalOpen] = useState(false);

  useEffect(() => {
    appraisalGridStore.fetchGridData();
  }, []);

  const openLinkAppraisalOrderOpenModal = orderId => {
    setOrderIdToLink(orderId || '');
    setLinkAppraisalOrderModalOpen(true);
  };

  function linkAppraisal(loanId, propertyId, orderId: string) {
    setConfirmLinkAppraisalOrderModalOpen(false);
    appraisalStore.linkAppraisalToLoan(loanId, propertyId, orderId);
    appraisalGridStore.resetAndFetchGridData();
  }

  function unlinkAppraisal(propertyAppraisalId) {
    setConfirmUnLinkAppraisalOrderModalOpen(false);
    appraisalStore.unlinkAppraisalFromLoan(propertyAppraisalId);
    appraisalGridStore.resetAndFetchGridData();
  }

  const openConfirmLinkAppraisalOrderModal = () => {
    setConfirmLinkAppraisalOrderModalOpen(true);
    setLinkAppraisalOrderModalOpen(false);
  };

  const actions = (record, handleMenuClose)=>{
    const actionsRowMenu = [
      {
        icon: LinkOff,
        text: 'Unlink order',
        onClickAction: () => setConfirmUnLinkAppraisalOrderModalOpen(true),
        showAction: true,
      },
      {
        icon: Link,
        text: 'Link order',
        onClickAction: () => openLinkAppraisalOrderOpenModal(record.orderId),
        showAction: true,
      },
    ];

    return (
      <>
        {actionsRowMenu.map(
          item => <MenuItem
            key={`menu_${item.text}`}
            onClick={()=>{
              item.onClickAction();
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <item.icon fontSize="small"/>
              <ListItemText>{item.text}</ListItemText>
            </ListItemIcon>
          </MenuItem>
        )}
        <StandardDialog
          open={isLinkAppraisalOrderModalOpen}
          handleClose={() => setLinkAppraisalOrderModalOpen(false)}
          title="Link Appraisal"
          dialogContent={
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={12}>
                <TextField
                  variant={'outlined'}
                  testId={'linkOrderId'}
                  standaloneLabel={true}
                  label={'Tamarisk Order Id'}
                  required
                  value={orderIdToLink}
                  onChange={e => setOrderIdToLink(e.target.value)}
                />
              </Grid>
            </Grid>
          }
          dialogActions={
            <Button
              testId={'linkOrderIdBtn'}
              onClick={openConfirmLinkAppraisalOrderModal}
              size="large"
              variant="contained"
              type="submit"
              color="primary"
            >
              Link
            </Button>
          }
        />
        <ConfirmDialog
          open={isConfirmLinkAppraisalOrderModalOpen}
          handleClose={() => setConfirmLinkAppraisalOrderModalOpen(false)}
          handleCancel={() => setConfirmLinkAppraisalOrderModalOpen(false)}
          handleConfirm={() =>
            linkAppraisal(record.loanId, record.propertyId, orderIdToLink)
          }
          title={'Confirmation'}
          body={'Are you sure want to link this order to the property?'}
        />
        <ConfirmDialog
          open={isConfirmUnLinkAppraisalOrderModalOpen}
          handleClose={() => setConfirmUnLinkAppraisalOrderModalOpen(false)}
          handleCancel={() => setConfirmUnLinkAppraisalOrderModalOpen(false)}
          handleConfirm={() =>
            unlinkAppraisal(record.propertyAppraisalId)
          }
          title={'Confirmation'}
          body={`Are you sure want to unlink this order ${record.orderId} from the property?`}
        />
      </>
    )
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
      setRecord(record);
      setAnchorEl(event.currentTarget);
    };

  const frameworkComponents = {
    actionCellRenderer: params => {
      return (
        <IconButton
            onClick={e => {
              openMenu(e, params.node.data);
            }}
          >
            <MoreVert color="primary" />
          </IconButton>
      );
    }
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    appraisalGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    appraisalGridStore.setFilterModel(filterModel);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <Grid item className={classes.gridContainer}>
        <DataGrid
          columns={APPRAISAL_DASHBOARD_COLUMNS}
          rows={appraisalGridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          isLoading={appraisalGridStore.isLoading}
          showFilters={appraisalGridStore.showFilters}
          sortModel={appraisalGridStore.sortModel}
          filterModel={appraisalGridStore.filterModel}
          paginationData={appraisalGridStore.gridData.meta}
          setPageNumber={appraisalGridStore.setPageNumber}
        />
      </Grid>
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        {actions(record, handleMenuClose)}
      </Menu>
    </Box>
  )
})